'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { KeyRound, Link2, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  SetupConnectFormActions,
  SetupConnectFormPanel,
} from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectedPlatformPanel } from '@/components/setup/experience/SetupConnectedPlatformPanel';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SetupWooOAuthGuidePanel } from '@/components/setup/experience/SetupWooOAuthGuidePanel';
import { SETUP_FIELD_INPUT_CLASS, SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { useSetupAttachPlatformIntegration } from '@/components/setup/experience/hooks/useSetupAttachPlatformIntegration';
import type { WooConnectTutorialMode } from '@/components/setup/experience/lib/connectTutorialContent';
import { buildWooCommerceOAuthUrl } from '@/components/setup/experience/lib/setupOAuthUtils';
import {
  createSetupWooManualSchema,
  createSetupWooOAuthSchema,
  type SetupWooManualFormValues,
  type SetupWooOAuthFormValues,
} from '@/components/setup/setupIntegrationSchemas';
import type { SetupSessionStore } from '@/components/setup/experience/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGetAccountInfo } from '@/lib/api/account';
import { useDeleteIntegration, useIntegrationsList } from '@/lib/api/integrations';
import { useValidateWoocommerceConnection } from '@/lib/api/setup-integrations';
import { ORG_ROUTES } from '@/config/router';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

/** Wraps the swapped field set so the mode toggle can declare what it controls (aria-controls). */
const WOO_FIELDS_REGION_ID = 'setup-xp-woo-fields';

interface SetupConnectWooStepProps {
  store: SetupSessionStore;
  orgSlug: string;
  oauthError?: string | null;
  /** Integration attached and verified (manual keys mode). */
  onConnected: () => void;
  /** Already connected (e.g. back-navigated from shipping) — advance forward again. */
  onContinue: () => void;
  onBack: () => void;
}

export function SetupConnectWooStep({
  store,
  orgSlug,
  oauthError,
  onConnected,
  onContinue,
  onBack,
}: SetupConnectWooStepProps) {
  const { t } = useTranslation(['setup', 'integrations']);
  const validateMutation = useValidateWoocommerceConnection();
  const attachMutation = useSetupAttachPlatformIntegration();
  const { data: accountInfo } = useGetAccountInfo();
  const [mode, setMode] = React.useState<WooConnectTutorialMode>('oauth');
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  // A WooCommerce integration may already exist — e.g. the merchant connected, advanced to
  // shipping, then stepped back. Surface the connected state with a disconnect action instead
  // of an empty form, mirroring Shopify and the integrations section.
  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsList(store.id);
  const deleteIntegration = useDeleteIntegration();
  const wooIntegration = React.useMemo(
    () =>
      integrations?.find((integration) => integration.provider === 'woocommerce' && integration.enabled) ??
      null,
    [integrations],
  );

  // The validation keys live in the 'integrations' namespace; `t` defaults to 'setup'.
  const oauthSchema = React.useMemo(() => createSetupWooOAuthSchema((key) => t(`integrations:${key}`)), [t]);
  const manualSchema = React.useMemo(
    () => createSetupWooManualSchema((key) => t(`integrations:${key}`)),
    [t],
  );

  const oauthForm = useForm<SetupWooOAuthFormValues>({
    resolver: zodResolver(oauthSchema),
    defaultValues: { url: '' },
  });

  const manualForm = useForm<SetupWooManualFormValues>({
    resolver: zodResolver(manualSchema),
    defaultValues: { url: '', consumerKey: '', consumerSecret: '' },
  });

  const isSubmitting = validateMutation.isPending || attachMutation.isPending || isRedirecting;

  const onOAuthSubmit = (data: SetupWooOAuthFormValues) => {
    if (!accountInfo?.id) return;
    setFormError(null);

    // wc-auth returns no params and its callback is server-to-server, so we carry the full nav
    // cursor plus the `awaiting` poll marker in the (client-controlled) return_url. On return the
    // wizard lands back on this connect step and polls the integrations list until it appears.
    const returnParams = new URLSearchParams({
      storeId: store.id,
      step: 'products',
      sub: 'connect',
      source: 'woocommerce',
      awaiting: 'woocommerce',
    });
    const setupReturnUrl = `${window.location.origin}${ORG_ROUTES(orgSlug).SETUP()}?${returnParams.toString()}`;
    const oauthRedirectUrl = buildWooCommerceOAuthUrl({
      shopUrl: data.url.trim(),
      storeId: store.id,
      accountId: accountInfo.id,
      returnUrl: setupReturnUrl,
    });

    setIsRedirecting(true);
    window.location.href = oauthRedirectUrl;
  };

  const onManualSubmit = async (data: SetupWooManualFormValues) => {
    setFormError(null);

    const url = data.url.trim();
    const consumerKey = data.consumerKey.trim();
    const consumerSecret = data.consumerSecret.trim();

    try {
      await validateMutation.mutateAsync({ url, consumerKey, consumerSecret });

      await attachMutation.mutateAsync({
        storeId: store.id,
        connection: { platform: 'woocommerce', mode: 'manual', url, consumerKey, consumerSecret },
      });

      onConnected();
    } catch (error) {
      const parsed = parseTRPCError(error);
      // Re-submitting after back-navigation: the server rejects duplicates but the store IS
      // connected — treat it as success so the user has a forward path.
      if (/already exists/i.test(parsed.message)) {
        onConnected();
        return;
      }
      setFormError(parsed);
    }
  };

  // OAuth is the primary path; manual API keys are a fallback for stores where wc-auth fails.
  // Carry the store URL across so switching methods never makes the user retype it, and move
  // focus into the freshly rendered field set so keyboard/screen-reader users aren't stranded
  // on the toggle while the inputs beside it are swapped out.
  const toggleMode = () => {
    setFormError(null);
    const next: WooConnectTutorialMode = mode === 'oauth' ? 'manual' : 'oauth';
    if (next === 'manual') {
      manualForm.setValue('url', oauthForm.getValues('url'));
    } else {
      oauthForm.setValue('url', manualForm.getValues('url'));
    }
    setMode(next);
    requestAnimationFrame(() => {
      if (next === 'manual') {
        manualForm.setFocus('url');
      } else {
        oauthForm.setFocus('url');
      }
    });
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (mode === 'oauth') {
      void oauthForm.handleSubmit(onOAuthSubmit)(event);
      return;
    }

    void manualForm.handleSubmit(onManualSubmit)(event);
  };

  // Wait for the first integrations read before deciding which view to show — avoids flashing
  // the empty form when the store is in fact already connected.
  if (isLoadingIntegrations && !integrations) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" aria-hidden />
      </div>
    );
  }

  if (wooIntegration) {
    const shopName =
      (wooIntegration.metadata?.shopName as string | undefined) ||
      (wooIntegration.credentials?.url as string | undefined) ||
      store.name ||
      t('integrations:woocommerce.yourStore');
    const connectedViaOAuth = Boolean(wooIntegration.metadata?.connectedAt);

    return (
      <div className="flex flex-col">
        <motion.div
          className="flex w-full max-w-none flex-col gap-0"
          initial={SETUP_PATH_MOTION.actions.initial}
          animate={SETUP_PATH_MOTION.actions.animate}
          transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.05 }}
        >
          <SetupConnectStepSection
            title={t('setup:experience.connect.wooTitle')}
            description={t('setup:experience.connect.connected.subtitle', {
              platform: t('integrations:names.woocommerce'),
            })}
            platform={{ logoSrc: '/integrations/woo-icon.svg', name: t('integrations:names.woocommerce') }}
            platformInTitle
            statusBadge={t('setup:experience.connect.connected.badge')}
            footer={
              <SetupStepFooter
                backLabel={t('setup:footer.back')}
                onBack={onBack}
                backDisabled={deleteIntegration.isPending}
                primaryLabel={t('setup:experience.connect.continue')}
                primaryType="button"
                onPrimary={onContinue}
                primaryDisabled={deleteIntegration.isPending}
              />
            }
          >
            <SetupConnectFormPanel>
              <SetupConnectedPlatformPanel
                platform="woocommerce"
                platformName={t('integrations:names.woocommerce')}
                shopName={shopName}
                connectedViaOAuth={connectedViaOAuth}
                disconnectConfirm={t('integrations:woocommerce.disconnectConfirm')}
                isDisconnecting={deleteIntegration.isPending}
                onDisconnect={() => deleteIntegration.mutate({ integrationId: wooIntegration.id })}
              />
            </SetupConnectFormPanel>
          </SetupConnectStepSection>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <motion.form
        className="flex w-full max-w-none flex-col gap-0"
        onSubmit={onSubmit}
        noValidate
        initial={SETUP_PATH_MOTION.actions.initial}
        animate={SETUP_PATH_MOTION.actions.animate}
        transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.05 }}
      >
        <SetupConnectStepSection
          title={t('setup:experience.connect.wooTitle')}
          description={t('setup:experience.connect.wooIntro')}
          platform={{ logoSrc: '/integrations/woo-icon.svg', name: t('integrations:names.woocommerce') }}
          platformInTitle
          footer={
            <SetupConnectFormActions
              backLabel={t('setup:footer.back')}
              onBack={onBack}
              submitLabel={
                mode === 'oauth'
                  ? t('setup:experience.connect.continue')
                  : t('setup:experience.connect.verify')
              }
              isSubmitting={isSubmitting}
              submitDisabled={mode === 'oauth' && !accountInfo?.id}
            />
          }
        >
          <SetupConnectFormPanel>
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:gap-8">
              <div className="flex flex-col gap-[0.875rem]">
                <div id={WOO_FIELDS_REGION_ID} className="flex flex-col gap-[0.875rem]">
                  {mode === 'oauth' ? (
                    <div className="flex flex-col gap-2">
                      <Label
                        htmlFor="setup-xp-woo-oauth-url"
                        className="text-muted-foreground text-sm font-medium leading-[1.3]"
                      >
                        {t('integrations:woocommerce.create.shopUrlLabel')}
                      </Label>
                      <Input
                        id="setup-xp-woo-oauth-url"
                        className={SETUP_FIELD_INPUT_CLASS}
                        placeholder={t('integrations:woocommerce.create.shopUrlPlaceholder')}
                        autoComplete="off"
                        aria-invalid={Boolean(oauthForm.formState.errors.url)}
                        {...oauthForm.register('url')}
                      />
                      {oauthForm.formState.errors.url ? (
                        <p className="text-destructive m-0 text-xs">
                          {oauthForm.formState.errors.url.message}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-2">
                        <Label
                          htmlFor="setup-xp-woo-url"
                          className="text-muted-foreground text-sm font-medium leading-[1.3]"
                        >
                          {t('integrations:woocommerce.create.shopUrlLabel')}
                        </Label>
                        <Input
                          id="setup-xp-woo-url"
                          className={SETUP_FIELD_INPUT_CLASS}
                          placeholder={t('integrations:woocommerce.create.shopUrlPlaceholder')}
                          autoComplete="off"
                          aria-invalid={Boolean(manualForm.formState.errors.url)}
                          {...manualForm.register('url')}
                        />
                        {manualForm.formState.errors.url ? (
                          <p className="text-destructive m-0 text-xs">
                            {manualForm.formState.errors.url.message}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label
                          htmlFor="setup-xp-woo-key"
                          className="text-muted-foreground text-sm font-medium leading-[1.3]"
                        >
                          {t('integrations:woocommerce.manual.consumerKeyLabel')}
                        </Label>
                        <Input
                          id="setup-xp-woo-key"
                          className={SETUP_FIELD_INPUT_CLASS}
                          autoComplete="off"
                          aria-invalid={Boolean(manualForm.formState.errors.consumerKey)}
                          {...manualForm.register('consumerKey')}
                        />
                        {manualForm.formState.errors.consumerKey ? (
                          <p className="text-destructive m-0 text-xs">
                            {manualForm.formState.errors.consumerKey.message}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label
                          htmlFor="setup-xp-woo-secret"
                          className="text-muted-foreground text-sm font-medium leading-[1.3]"
                        >
                          {t('integrations:woocommerce.manual.consumerSecretLabel')}
                        </Label>
                        <Input
                          id="setup-xp-woo-secret"
                          className={SETUP_FIELD_INPUT_CLASS}
                          type="password"
                          autoComplete="new-password"
                          aria-invalid={Boolean(manualForm.formState.errors.consumerSecret)}
                          {...manualForm.register('consumerSecret')}
                        />
                        {manualForm.formState.errors.consumerSecret ? (
                          <p className="text-destructive m-0 text-xs">
                            {manualForm.formState.errors.consumerSecret.message}
                          </p>
                        ) : null}
                      </div>

                      {oauthError ? (
                        <p className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs">
                          {oauthError}
                        </p>
                      ) : null}

                      {formError && !formError.isValidationError ? (
                        <p className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs">
                          {formError.message}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>

                <button
                  type="button"
                  aria-controls={WOO_FIELDS_REGION_ID}
                  className="text-muted-foreground hover:text-foreground inline-flex w-fit cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[0.8125rem] font-medium underline underline-offset-2"
                  onClick={toggleMode}
                >
                  {mode === 'oauth' ? (
                    <KeyRound className="size-3.5" aria-hidden />
                  ) : (
                    <Link2 className="size-3.5" aria-hidden />
                  )}
                  {mode === 'oauth'
                    ? t('setup:experience.connect.wooUseManual')
                    : t('setup:experience.connect.wooUseOAuth')}
                </button>
              </div>

              <SetupWooOAuthGuidePanel mode={mode} />
            </div>
          </SetupConnectFormPanel>
        </SetupConnectStepSection>
      </motion.form>
    </div>
  );
}
